import { mapDTO } from '@twistezo/ts-dto-mapper'
import { omit } from 'lodash'

import { fieldsToOmit } from 'providers/tanstack-query/utils'
import { floatPercentToDTO } from 'utils/number'

import type { Variables } from './types'
import type { Schema } from 'features/inventory/ad-unit/forms/UnfilledRecoveryForm/schema'
import type { Ability } from 'providers/casl'

type From = Schema
type To = Variables

export const toDTO = (from: From, ability: Ability): To =>
  mapDTO<From, To>({ from }).transform(
    ({
      allowMultiformatEnabled,
      baseAdUnitPath,
      customizeBaseAdUnitPathEnabled,
      customizePrebidBehaviour,
      desktopAdDisplaysBeforeRefresh,
      desktopSingleAdViewTime,
      id,
      mobileAdDisplaysBeforeRefresh,
      mobileSingleAdViewTime,
      prebidBehaviourType,
      rotatingAdsQty,
      unfilledRecoveryEnabled,
      urDesktopAdThreshold,
      urMobileAdThreshold,
    }: From): To => {
      const constantFields: Pick<
        To,
        | 'adUnitId'
        | 'allowMultiformatEnabled'
        | 'customizePrebidBehaviour'
        | 'prebidBehaviourType'
        | 'unfilledRecoveryEnabled'
      > = {
        adUnitId: id,
        allowMultiformatEnabled,
        customizePrebidBehaviour,
        ...(customizePrebidBehaviour ? { prebidBehaviourType } : {}),
        unfilledRecoveryEnabled,
      }

      const variableFields: Pick<
        To,
        | 'baseAdUnitPath'
        | 'customizeBaseAdUnitPathEnabled'
        | 'desktopAdDisplaysBeforeRefresh'
        | 'desktopSingleAdViewTime'
        | 'mobileAdDisplaysBeforeRefresh'
        | 'mobileSingleAdViewTime'
        | 'rotatingAdsQty'
        | 'urDesktopAdThreshold'
        | 'urMobileAdThreshold'
      > = {
        baseAdUnitPath,
        customizeBaseAdUnitPathEnabled,
        desktopAdDisplaysBeforeRefresh: Number(desktopAdDisplaysBeforeRefresh),
        desktopSingleAdViewTime,
        mobileAdDisplaysBeforeRefresh: Number(mobileAdDisplaysBeforeRefresh),
        mobileSingleAdViewTime,
        rotatingAdsQty: Number(rotatingAdsQty),
        urDesktopAdThreshold: floatPercentToDTO(urDesktopAdThreshold),
        urMobileAdThreshold: floatPercentToDTO(urMobileAdThreshold),
      }

      return {
        ...constantFields,
        ...omit(
          variableFields,
          fieldsToOmit<Variables>({
            baseAdUnitPath: ability.cannot('update', 'AdUnitFeature-unfilledRecoveryBasicSettings'),
            customizeBaseAdUnitPathEnabled: ability.cannot(
              'update',
              'AdUnitFeature-unfilledRecoveryBasicSettings',
            ),
            desktopAdDisplaysBeforeRefresh: ability.cannot(
              'update',
              'AdUnitFeature-unfilledRecoveryMultiFormats',
            ),
            desktopSingleAdViewTime: ability.cannot(
              'update',
              'AdUnitFeature-unfilledRecoveryMultiFormats',
            ),
            mobileAdDisplaysBeforeRefresh: ability.cannot(
              'update',
              'AdUnitFeature-unfilledRecoveryMultiFormats',
            ),
            mobileSingleAdViewTime: ability.cannot(
              'update',
              'AdUnitFeature-unfilledRecoveryMultiFormats',
            ),
            rotatingAdsQty: ability.cannot('update', 'AdUnitFeature-unfilledRecoveryMultiFormats'),
            urDesktopAdThreshold: ability.cannot(
              'update',
              'AdUnitFeature-unfilledRecoveryMultiFormats',
            ),
            urMobileAdThreshold: ability.cannot(
              'update',
              'AdUnitFeature-unfilledRecoveryMultiFormats',
            ),
          }),
        ),
      }
    },
  )
